/**
 * 画面を開くときに、URL で頼まれた年月（`?year=2026&month=9`）。
 *
 * ## なぜ要るのか
 *
 * 勤務表・業務報告・経費精算・通勤費精算は、開いたときの月を自分で決める
 * （毎月9日までは前月、10日からは今月）。ダッシュボードの「月次提出状況」は
 * 会社の「提出物の締め」で決まる月（締め中の月）の状態を出すので、
 * **締めを第5営業日や15日にしている会社では、行の月と、押した先で開く月が
 * 食い違う**（「9月分 未申請」の行を押すと10月の画面が開く）。
 * ダッシュボードの行は年月を付けて開き、開いた画面はその月で始める。
 *
 * ## 無いとき・読めないとき
 *
 * `null` を返す。呼ぶ側は今までどおりの月で開く（左メニューから開いたときの
 * 動きは変えない）。
 *
 * @param {object} route Vue Router の `$route`
 * @returns {{year: number, month: number}|null}
 */
export function askedYearMonth (route) {
  const query = (route && route.query) || {}
  if (!/^\d{4}$/.test(String(query.year)) || !/^\d{1,2}$/.test(String(query.month))) return null
  const year = Number(query.year)
  const month = Number(query.month)
  if (year < 2000 || year > 2100 || month < 1 || month > 12) return null
  return { year, month }
}

/**
 * yyyyMM を、上の形の query（文字列）にする。読めなければ `null`。
 *
 * @param {string} yearMonth yyyyMM
 * @returns {{year: string, month: string}|null}
 */
export function yearMonthQuery (yearMonth) {
  const text = String(yearMonth || '')
  if (!/^\d{6}$/.test(text)) return null
  const query = { year: text.substring(0, 4), month: String(Number(text.substring(4, 6))) }
  return askedYearMonth({ query }) ? query : null
}

/**
 * 頼まれた年月で開く画面（mounted で `askedYearMonth` を読む画面）。
 *
 * **ここに無い画面へ年月を付けても、読まれない。**ダッシュボードの行は、行き先が
 * ここにあるときだけ年月を付ける（自分の申請一覧・承認待ちの申請・休暇申請詳細は
 * 年月を取らないので、今までどおりの行き先のまま）。
 * 画面を足すときは、その画面の mounted で `askedYearMonth` を読むようにしてから、ここへ足す
 * （tests/unit/dashboard/dashboardRowsOpenTheRowsMonth.spec.js が、ここの画面を1つずつ開いて確かめる）。
 */
export const SCREENS_OPENING_ASKED_MONTH = Object.freeze([
  '/report/roster',
  '/report/monthlyreport',
  '/payoff/expense',
  '/payoff/travelCost'
])

/**
 * 行き先に、その行の月を付ける。
 *
 * 行き先が年月を読む画面で、月（yyyyMM）が読めるときだけ `{ path, query }` を返す。
 * それ以外は、渡された行き先をそのまま返す（今までどおり月なしで開く）。
 *
 * @param {string} path 行き先（例 `/report/roster`）
 * @param {string} yearMonth その行が話している月（yyyyMM）
 * @returns {string|{path: string, query: {year: string, month: string}}}
 */
export function routeWithYearMonth (path, yearMonth) {
  if (SCREENS_OPENING_ASKED_MONTH.indexOf(path) < 0) return path
  const query = yearMonthQuery(yearMonth)
  return query ? { path, query } : path
}

export default askedYearMonth
